/* eslint-disable simple-import-sort/imports */
import * as Sentry from "@sentry/browser";
import {
  EventType,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
/* global process */
import config from "config";
import { createRoot } from "react-dom/client";
import PrimeReact from "primereact/api";
import React from "react";
import { Provider } from "react-redux";
import { HashRouter as Router } from "react-router-dom";

import { userActions } from "./actions";
import { alertActions } from "./actions/alert.actions";
import App from "./app";
import {
  newPwdRequest,
  SILENT_REDIRECT_URI,
  ssoRequest,
} from "./authConfig";
import { userConstants } from "./constants";
import { history as appHistory } from "./helpers";
import {
  buildAcquireRequest,
  finalizeAuthenticatedSession,
  isOnUsersRouteHash,
} from "./helpers/auth-finalize";
import {
  clearAuthRedirectPending,
  isAuthRedirectPending,
  setAuthRedirectPending,
} from "./helpers/authRedirectPending";
import { setSessionClientId } from "./helpers/client";
import { getIsDeepLinkE2eSession } from "./helpers/cypressE2eFlags";
import {
  handleDeployStaleAssetFailure,
  isChunkLoadFailureReason,
  isDeployStaleAssetUrl,
  stripCacheBustParamFromUrl,
} from "./helpers/deployStaleAssetRecovery";
import { rewriteMsalPathStyleOAuthHash } from "./helpers/msalPathStyleHash";
import { getIsCypressAut } from "./helpers/isCypressAut";
import { getSessionMsalAccount } from "./helpers/msalSession";
import { DEEP_LINK_HISTORY_MSAL_HOME_ACCOUNT_KEY } from "./helpers/postAuthGrace";
import { rewriteLegacyExchangesHashIfNeeded } from "./views/Exchanges/utils/urlUtils";
import msalInstance, { msalReady } from "./msalInstance";
import { store } from "./store";
import { MsalPathStyleHashFix } from "./components/MsalPathStyleHashFix";

// Prevent re-entrant login handling caused by our own proactive token fetch
let suppressNextAcquireTokenSuccess = false;
let postRedirectHandled = false;

const isMsalInteractionRequiredError = (error) => {
  if (!error) return false;
  if (error instanceof InteractionRequiredAuthError) return true;
  const blob = `${error.errorCode || ""} ${error.errorMessage || ""} ${
    error.subError || ""
  } ${error.message || ""} ${String(error)}`;
  return /interaction_required|AADB2C90077/i.test(blob);
};

/** Hosts that often fail from blockers / tracking protection (not app bugs). */
const isThirdPartyAnalyticsResourceUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  return (
    /clarity\.ms/i.test(url) ||
    /googletagmanager\.com/i.test(url) ||
    /google-analytics\.com/i.test(url) ||
    /gstatic\.com.*translate/i.test(url)
  );
};

Sentry.init({
  dsn: "https://be7c7f98914843979290152521acd605@sentry.io/2004315",
  environment: process.env.REACT_APP_ENVIRONMENT,
  release: process.env.REACT_APP_RELEASE,
  beforeBreadcrumb(breadcrumb, hint) {
    if (breadcrumb.category === "ui.click") {
      const { target } = hint.event;
      if (target.ariaLabel) {
        breadcrumb.message = target.ariaLabel;
      }
    }
    //filter out console logging and/or console.something of successful calls to ensure it's small enough to not get 413 status back- important stuff is in console.errors
    if (
      breadcrumb.category === "console" &&
      (breadcrumb.level === "log" ||
        breadcrumb.message.includes('"status":200'))
    ) {
      return null;
    }
    return breadcrumb;
  },
  beforeSend(event, hint) {
    const message =
      event?.exception?.values?.[0]?.value ||
      (hint?.originalException && String(hint.originalException)) ||
      "";

    // Drop expected MSAL “no session” silent failures
    if (
      message.includes("AADB2C90077") || // no session + prompt=none
      message.includes("interaction_required")
    ) {
      return null;
    }

    // Logout can race MSAL redirect teardown; deferred logout + fallback
    // addresses this — any remaining BrowserAuthError is not actionable (2ET).
    try {
      const orig = hint && hint.originalException;
      const origStr = orig ? String(orig) : "";
      const blob = `${message} ${origStr}`;
      if (
        /BrowserAuthError/i.test(blob) &&
        /interaction_in_progress/i.test(blob)
      ) {
        return null;
      }
    } catch (_e) {
      // ignore
    }

    // Drop benign MSAL hidden-iframe silent auth failures that are expected in
    // browsers with 3rd-party cookies/tracking protection (e.g., Safari/Firefox)
    try {
      const lower = String(message).toLowerCase();
      const iframeNoise = [
        "monitor_window_timeout", // msal browser auth error on iframe timeout
        "monitor window timeout",
        "hash_timeout",
        "frame_load_error",
        "frame load error",
        "x-frame-options",
        "blocked a frame with origin",
        "third-party cookies",
        "third party cookies",
        // Added: Drop benign MSAL parse error when hash is gone by the time iframe parses
        "hash_does_not_contain_known_properties",
      ];
      if (iframeNoise.some((s) => lower.includes(s))) {
        return null;
      }
    } catch (_e) {
      // ignore filtering errors
    }

    const capturedMsg =
      typeof event?.message === "string" ? event.message : "";
    if (
      (capturedMsg === "Resource load failure" ||
        capturedMsg === "Resource load failure (stylesheet)") &&
      isThirdPartyAnalyticsResourceUrl(event?.tags?.url)
    ) {
      return null;
    }

    // Refined Axios 401 handling:
    // - Drop during auth/logout routes to avoid noise
    // - Keep repeated 401s (after a retry) as errors
    // - Downgrade other 401s to warning so they're visible but less noisy
    // - Also handle "Non-Error promise rejection" when value is the string
    //   "Request failed with status code 401" (unhandled 401 from axios callers)
    try {
      const original = hint && hint.originalException;
      const isAxiosError =
        original &&
        (original.isAxiosError || original.name === "AxiosError");
      const status =
        original && original.response && original.response.status;
      const is401StringRejection =
        message.includes("Request failed with status code 401") ||
        message.includes("status code 401");
      const is401 =
        (isAxiosError && status === 401) || is401StringRejection;

      if (is401) {
        const hash =
          (typeof window !== "undefined" &&
            window.location &&
            window.location.hash) ||
          "";
        const inAuthRoute = /#\/login|#\/loggingIn|#\/logout/i.test(hash);
        const loggingOut = !!store.getState()?.authentication?.loggingOut;
        const retriedOnce = !!(
          original &&
          original.config &&
          original.config._retry
        );

        if (inAuthRoute || loggingOut) {
          return null;
        }
        if (retriedOnce && isAxiosError) {
          return event; // meaningful failure after refresh
        }
        event.level = "warning";
        event.tags = {
          ...(event.tags || {}),
          httpStatus: "401",
          errorCategory: "unauthorized",
        };
        return event;
      }
    } catch (_e) {
      // ignore filtering errors
    }

    return event;
  },
});

// Stale HTML after deploy can 404 hashed chunks — recover or show error UI
// instead of an infinite Suspense spinner (DE-9408 / ADSWEBUI-REACT-2DJ).
const reportDeployAssetFailureExhausted = ({
  message,
  resourceType,
  url,
  extra,
} = {}) => {
  Sentry.captureMessage(message || "Resource load failure", {
    level: "warning",
    tags: {
      resourceType: resourceType || "unknown",
      recoveryStatus: "exhausted",
      ...(url ? { url, referrer: document.referrer || "" } : {}),
    },
    extra,
  });
};

// First-party script/stylesheet recovery is owned by
// public/asset-load-error-handler.v2.js (loads before hashed bundles). This
// listener only reports non-stale / non-analytics resource failures to Sentry.
window.addEventListener(
  "error",
  (event) => {
    try {
      const target = event?.target;
      if (!target) return;
      const isScript = target instanceof HTMLScriptElement;
      const isLink =
        target instanceof HTMLLinkElement && target.rel === "stylesheet";
      if (!isScript && !isLink) return;

      const url = isScript ? target.src : isLink ? target.href : "";
      if (!url) return;

      if (isThirdPartyAnalyticsResourceUrl(url)) {
        return;
      }

      // Early handler already recovered (or is recovering) — do not double-count.
      if (
        window.__adsEarlyDeployRecoveryHandled ||
        window.__adsDeployRecoveryInFlight ||
        isDeployStaleAssetUrl(url, { isScript, isLink })
      ) {
        return;
      }

      Sentry.captureMessage("Resource load failure", {
        level: "error",
        tags: {
          resourceType: isScript ? "script" : "style",
          url,
          referrer: document.referrer || "",
          recoveryStatus: "not_applicable",
        },
      });
    } catch (_e) {
      // ignore
    }
  },
  true,
);

// Capture chunk load rejections (e.g., dynamic import failures)
window.addEventListener("unhandledrejection", (event) => {
  try {
    const reason = event?.reason;
    if (!reason || !isChunkLoadFailureReason(reason)) return;
    const msg = String(reason && (reason.message || reason));
    handleDeployStaleAssetFailure({
      reportExhausted: () =>
        reportDeployAssetFailureExhausted({
          message: "Chunk load failure",
          resourceType: "chunk",
          extra: {
            message: msg,
            stack:
              reason && reason.stack
                ? String(reason.stack).slice(0, 2000)
                : undefined,
          },
        }),
    });
  } catch (_e) {
    // ignore
  }
});

// Initialize MSAL and set up everything after initialization
msalReady
  .then(() => {
    const isCypressRuntime = getIsCypressAut();

    // no-op
    // If we landed here with an MSAL interaction_required error in the URL,
    // fail fast back to the login page instead of hanging on the waiting screen
    try {
      const decodedHash = decodeURIComponent(window.location.hash || "");
      if (
        !isCypressRuntime &&
        (/error=interaction_required/i.test(decodedHash) ||
          /AADB2C90077/i.test(decodedHash))
      ) {
        store.dispatch(userActions.logout());
        window.location.hash = "#/login";
      }
      // If we just returned from a forced SSO reset, inform the user
      if (/resetSSO=1/i.test(decodedHash)) {
        try {
          if (typeof sessionStorage !== "undefined") {
            sessionStorage.setItem("ads.resetSSONotice", "1");
          }
        } catch (_e) {
          // ignore storage errors
        }
        // Clean the URL to avoid re-triggering in router
        if (decodedHash.startsWith("#/login")) {
          window.location.hash = "#/login";
        }
      }
    } catch (_e) {
      // ignore URL parsing issues
    }
    // Ensure msal-react is notified when accounts are added/removed via cache seeding in tests
    if (typeof msalInstance.enableAccountStorageEvents === "function") {
      msalInstance.enableAccountStorageEvents();
    }

    // Only auto-select when a single cached account exists; never pick [0] when
    // multiple stale accounts remain after logout (wrong-user finalize).
    const soleCachedAccount = getSessionMsalAccount(msalInstance);
    if (soleCachedAccount && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(soleCachedAccount);
    }

    // Attempt silent SSO on startup only when we have a user hint
    const loginHint = (() => {
      try {
        return localStorage.getItem("ads.loginHint") || "";
      } catch (_e) {
        return "";
      }
    })();
    const startupHash = String(window.location.hash || "");
    const onLoginHash = /^#\/login\b/i.test(startupHash);
    // Cypress seeds session via ROPC + cache; ssoSilent often hits AADB2C90077 and
    // must not run (event handler would clear Redux when getActiveAccount is empty).
    if (
      !isCypressRuntime &&
      !msalInstance.getActiveAccount() &&
      loginHint &&
      !onLoginHash &&
      !isAuthRedirectPending()
    ) {
      msalInstance
        .ssoSilent({
          ...ssoRequest,
          loginHint,
          redirectUri: SILENT_REDIRECT_URI,
        })
        .catch(() => {
          // Ignore; fallback to unauthenticated UI
        });
    }

    // Startup guard: if MSAL has an account but Redux/storage user is missing,
    // proactively attempt to finalize session to avoid falling through to Holding/Login.
    try {
      const sessionAccount = getSessionMsalAccount(msalInstance);
      const reduxUser = store.getState().authentication.user;
      if (
        !getIsCypressAut() &&
        !getIsDeepLinkE2eSession() &&
        sessionAccount &&
        !reduxUser &&
        !onLoginHash &&
        !isAuthRedirectPending()
      ) {
        const active = sessionAccount;
        const request = {
          scopes: ["openid", ...config.adsMsalConfig.b2cScopes],
          account: active,
          forceRefresh: false,
          redirectUri: SILENT_REDIRECT_URI,
        };
        // Avoid navigating away from current hash; if token is acquired, let event handler complete flow
        msalInstance.acquireTokenSilent(request).catch(() => {
          // ignore; normal UI will proceed
        });
      }
    } catch (_e) {
      // ignore guard errors
    }

    // Removed waiting-screen poll to avoid any indirect hash bouncing.

    // Set up event callback after initialization
    msalInstance.addEventCallback((event) => {
      const hasUser = !!store.getState().authentication.user;

      // Visual flow note: do not mutate location.hash here or we may clear the auth fragment before MSAL parses it
      if (event.eventType === EventType.HANDLE_REDIRECT_START) {
        console.info("AuthFlowDebug: redirect_start");
      }

      // After redirect completes, if we don't yet have a Redux user, guide UX once, without forcing login loops.
      if (event.eventType === EventType.HANDLE_REDIRECT_END) {
        const stillNoUser = !store.getState().authentication.user;
        if (stillNoUser && !postRedirectHandled) {
          postRedirectHandled = true;
          try {
            const hasAccount = msalInstance.getAllAccounts().length > 0;
            // Cypress seeds ROPC + Redux; do not send AUT to WaitingLogin (silent iframe fails).
            if (hasAccount && !isCypressRuntime) {
              const hash = String(window.location.hash || "");
              if (!/^(#\/login|#\/loggingIn)/.test(hash)) {
                window.location.hash = "#/loggingIn";
              }
            } else {
              // Do not force navigation here; let Login handle unauthenticated state to avoid loops
            }
          } catch (_e) {
            // Fallback conservatively to login
            // Avoid hash churn; Login screen will be shown by router
          }
          // Keep guard briefly to avoid re-entry; WaitingLogin completes or times out
          setTimeout(() => {
            postRedirectHandled = false;
          }, 30000);
        }
      }

      // Skip handling the next acquire success if it was initiated by our proactive fetch
      if (
        (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          event.eventType === EventType.SSO_SILENT_SUCCESS) &&
        suppressNextAcquireTokenSuccess
      ) {
        suppressNextAcquireTokenSuccess = false;
        return;
      }

      // If SSO_SILENT succeeds but Redux user is not yet set, show WaitingLogin to complete finalize
      if (
        event.eventType === EventType.SSO_SILENT_SUCCESS &&
        !hasUser &&
        !isCypressRuntime
      ) {
        if (!window.location.hash.includes("#/loggingIn")) {
          window.location.hash = "#/loggingIn";
        }
        return;
      }
      //if cancellation of forgot password
      if (
        appHistory.location.hash.includes(
          "error_description=AADB2C90091%3a+The+user+has+cancelled",
        )
      ) {
        window.location.href = "#/login";
      }
      //if there's an external link to a particular exchange/order
      else if (
        !getIsCypressAut() &&
        !getIsDeepLinkE2eSession() &&
        appHistory.location.hash.includes("=") &&
        appHistory.location.hash.includes("?")
      ) {
        sessionStorage.setItem("history", appHistory.location.hash);
        try {
          sessionStorage.setItem(
            DEEP_LINK_HISTORY_MSAL_HOME_ACCOUNT_KEY,
            msalInstance.getActiveAccount()?.homeAccountId ?? "",
          );
        } catch (_e) {
          // ignore storage errors
        }
      }

      //logout again once they've changed their password
      else if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload.authority.includes("b2c_1_emailpasswordreset")
      ) {
        store.dispatch(userActions.msalLogout());
      } else if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
          event.eventType === EventType.SSO_SILENT_SUCCESS) &&
        event.payload &&
        event.payload.accessToken
      ) {
        // Only process non-LOGIN events if we don't already have a user
        if (event.eventType === EventType.LOGIN_SUCCESS || !hasUser) {
          // E2E seeds ROPC + Redux via Cypress; MSAL may still emit ACQUIRE_TOKEN_SUCCESS
          // before Redux has the user. Running finalize then (with stale sessionStorage
          // "history") can overwrite the hash (e.g. → #/inventory/mystock). Never run
          // MSAL-driven finalize in the Cypress AUT — support/e2e.js sets __ADS_CYPRESS_AUT.
          if (
            getIsCypressAut() ||
            getIsDeepLinkE2eSession() ||
            isOnUsersRouteHash()
          ) {
            return;
          }
          handleLogin(event);
        }
      } else if (event.eventType === EventType.LOGIN_SUCCESS) {
        // ROPC / E2E: never run silent acquire + finalize (can restore stale "history").
        if (
          getIsCypressAut() ||
          getIsDeepLinkE2eSession() ||
          isOnUsersRouteHash()
        ) {
          return;
        }
        // Ensure active account is set, but do not process login until we have an access token
        if (event.payload && event.payload.account) {
          msalInstance.setActiveAccount(event.payload.account);
          // Immediately acquire an API token after login success (often returns only ID token)
          const lastType = (function () {
            try {
              return localStorage.getItem("ads.auth.lastLoginType");
            } catch (_e) {
              return null;
            }
          })();
          const request = buildAcquireRequest(
            event.payload.account,
            lastType,
          );
          console.info(
            `AuthFlowDebug: login_success - attempting silent acquire username=${event.payload.account?.username} lastType=${lastType} authority=${request.authority}`,
          );
          msalInstance
            .acquireTokenSilent(request)
            .then((response) => {
              if (response && response.accessToken) {
                console.info(
                  "AuthFlowDebug: login_success - acquired access token",
                );
                try {
                  localStorage.removeItem("ads.auth.lastLoginType");
                } catch (_e) {
                  // ignore storage errors
                }
                if (
                  getIsCypressAut() ||
                  getIsDeepLinkE2eSession() ||
                  isOnUsersRouteHash()
                ) {
                  return undefined;
                }
                return finalizeAuthenticatedSession(
                  event.payload.account,
                  response.accessToken,
                );
              }
            })
            .catch(() => {
              // ignore; login screen will remain until token is available
              console.warn(
                "AuthFlowDebug: login_success - silent acquire failed",
              );
            });
        }
      } else if (
        event.eventType === EventType.SSO_SILENT_FAILURE ||
        (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE &&
          isMsalInteractionRequiredError(event.error))
      ) {
        // E2E uses ROPC + storage; silent MSAL may fail with no iframe session.
        // Never clear Redux here or Cypress loses the seeded user mid-test.
        if (isCypressRuntime) {
          return;
        }
        // Firefox commonly blocks 3rd-party cookies which breaks prompt=none.
        // To match other browsers' UX without altering flows, just ignore
        // the failure in Firefox so we don't spin on Login.
        try {
          const isFirefox =
            typeof navigator !== "undefined" &&
            /firefox/i.test(navigator.userAgent);
          if (isFirefox) {
            return;
          }
        } catch (_e) {
          // ignore UA parsing issues
        }
        // Prevent repeated handling loops across all browsers: handle once per session
        try {
          if (
            typeof sessionStorage !== "undefined" &&
            sessionStorage.getItem("ads.auth.ssoSilentFailureHandled") ===
              "1"
          ) {
            return;
          }
          sessionStorage.setItem("ads.auth.ssoSilentFailureHandled", "1");
        } catch (_e) {
          // ignore storage errors
        }
        // During redirect handoff or while showing WaitingLogin, ignore silent failures
        if (
          window.location.hash.includes("#/loggingIn") ||
          postRedirectHandled
        ) {
          return;
        }
        // If there is no active MSAL account, treat as unauthenticated even if Redux still holds a stale user
        const hasActiveAccount = !!msalInstance.getActiveAccount();
        if (!hasActiveAccount) {
          clearAuthRedirectPending();
          setSessionClientId();
          store.dispatch(failure(userConstants.MSAL_LOGIN_FAILURE));
          store.dispatch(failure(userConstants.EMAIL_LOGIN_FAILURE));
        }
        // Otherwise, ignore
      } else if (event.eventType === EventType.LOGIN_FAILURE) {
        console.error("login error: ", event.error);
        setSessionClientId();
        // If we hit an Azure B2C authorize 400 (usually due to oversized Cookie header),
        // programmatically clear B2C cookies via front-channel logout, then return to login.
        try {
          const msg = String(event.error || "");
          const looksLikeAuthorize400 =
            /\b400\b|Bad Request|authorize\?/i.test(msg);
          if (looksLikeAuthorize400) {
            const active = msalInstance.getAllAccounts()[0] || null;
            const rawClaim =
              (active?.idTokenClaims &&
                (active.idTokenClaims.tfp || active.idTokenClaims.acr)) ||
              "";
            const isEmailPolicy =
              typeof rawClaim === "string" &&
              /email/i.test(String(rawClaim));
            const baseAuthority = isEmailPolicy
              ? config.emailMsalConfig?.authority ||
                config.adsMsalConfig.authority
              : config.adsMsalConfig.authority;
            const normalized = String(baseAuthority).replace(/\/$/, "");
            const logoutUrl = `${normalized}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(
              `${window.location.origin}/#/login?resetSSO=1`,
            )}`;
            window.location.href = logoutUrl;
            return; // stop further handling; navigation will occur
          }
        } catch (_e) {
          // ignore detection/URL build errors and fall through
        }

        //forgot password
        if (event.error.toString().includes("AADB2C90118")) {
          // FIXED: Password reset calls now properly handled (already initialized)
          if (window.parent !== window) {
            msalInstance.loginPopup(newPwdRequest);
          } else {
            setAuthRedirectPending();
            msalInstance.loginRedirect(newPwdRequest);
          }
        } else {
          clearAuthRedirectPending();
          store.dispatch(failure(userConstants.MSAL_LOGIN_FAILURE));
          store.dispatch(alertActions.error(`${event.error}`));

          //if failure to login, try clearing all cookies of the domain
          let cookies = document.cookie.split("; ");

          for (var c = 0; c < cookies.length; c++) {
            var d = window.location.hostname.split(".");
            while (d.length > 0) {
              // eslint-disable-next-line security/detect-object-injection
              const cookiePair = cookies[c];
              let leftSide = "";
              if (typeof cookiePair === "string") {
                leftSide = cookiePair.split(";")[0];
              }
              const rawCookieName = leftSide.split("=")[0] || "";
              const safeCookieName = encodeURIComponent(
                rawCookieName.replace(/[^\w\-.]/g, ""),
              );
              var cookieBase =
                safeCookieName +
                "=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=" +
                d.join(".") +
                " ;path=";
              var p = location.pathname.split("/");
              document.cookie = cookieBase + "/";
              while (p.length > 0) {
                document.cookie = cookieBase + p.join("/");
                p.pop();
              }
              d.shift();
            }
          }
          //reload the page so that cookie consent will reappear
          document.location.reload(true);
        }
      }
    });
  })
  .catch((error) => {
    console.error("Failed to initialize MSAL:", error);
  });

// deprecated: previously used single-shot Identity fetch without retry
function failure(error) {
  return { type: userConstants.MSAL_LOGIN_FAILURE, error };
}

function handleLogin(event) {
  const account = event.payload.account;
  msalInstance.setActiveAccount(account);
  finalizeAuthenticatedSession(account, event.payload.accessToken).catch(
    (error) => {
      console.log("login error: ", error);
      clearAuthRedirectPending();
      setSessionClientId();
      store.dispatch(failure(userConstants.MSAL_LOGIN_FAILURE));
      store.dispatch(alertActions.error(`${error}`));
    },
  );
}

//undefined on local
console.log("process.env.REACT_APP_RELEASE");
console.log(process.env.REACT_APP_RELEASE);
console.log("public", process.env.PUBLIC_URL);
window.appVersion = process.env.REACT_APP_RELEASE;
PrimeReact.ripple = true;

// Legacy PBH deep links (#/orders/pbh?...) → #/exchanges?... before Router mounts.
rewriteLegacyExchangesHashIfNeeded();
// Path-style OAuth hashes for MSAL (HashRouter may re-slash after this; see MsalPathStyleHashFix).
rewriteMsalPathStyleOAuthHash();

// React 18 root setup
const container = document.getElementById("root");
const root = container ? createRoot(container) : null;

if (root) {
  stripCacheBustParamFromUrl();
  root.render(
    <Router>
      <MsalPathStyleHashFix />
      <Provider store={store}>
        <App pca={msalInstance} />
      </Provider>
    </Router>,
  );
}

// (Removed) Cypress-only visual markers and outlines
